import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';
import isEqualWith from 'lodash-es/isEqualWith';
import isNil from 'lodash-es/isNil';
import { useMemo } from 'react';
import { useClient } from '../../../ClientProvider';
import { mergeQueryState } from '../../../mergeState';
import useQuery from '../../../useQuery';
/**
 * Less than or equal to 0 note means null
 */
const isEcovitiNote = (x) => x > 0;
/**
 * Checks all "notes" fields from a decitrait campaign day to see if it passes {@link isEcovitiNote} test
 */
const hasAtLeastOneEcovitiNote = (decitraitSummaryItem) => [
    decitraitSummaryItem.mildew.notes.grapes,
    decitraitSummaryItem.mildew.notes.leaves,
    decitraitSummaryItem.oidium.notes.leaves,
    decitraitSummaryItem.oidium.notes.grapes
].some(isEcovitiNote);
/**
 * Checks for deep equality between two notes
 * See {@link DecitraitSanitaryStateDaysNotes}
 */
const isSameNote = (noteA, noteB) => !isNil(noteA) && !isNil(noteB) && isEqual(noteA.ssNotes, noteB.ssNotes);
/**
 * Fetches the decitrait summary and then finds the days with notes
 */
export const useDecitraitSanitaryStateNotes = ({ plotId }) => {
    const client = useClient();
    const summaryRequest = useQuery(client.decitrait.summary({ plotId }), {
        enabled: !isNil(plotId)
    });
    const plotRequest = useQuery(client.plots.get(plotId), {
        enabled: !isNil(plotId)
    });
    const plot = plotRequest.data;
    const decitraitSummary = summaryRequest.data || [];
    /**
     * Reducing the decitrait summary elements into {@link DecitraitSanitaryStateDaysNotes}
     */
    const sanitaryStateNotes = useMemo(() => {
        return decitraitSummary.reduce((acc, value) => {
            if (hasAtLeastOneEcovitiNote(value)) {
                const previousNote = !isEmpty(acc) ? acc[acc.length - 1] : undefined;
                const newNote = {
                    date: value.date,
                    ssNotes: {
                        mildewLeaves: isEcovitiNote(value.mildew.notes.leaves)
                            ? `${value.mildew.notes.leaves}`
                            : undefined,
                        mildewGrapes: isEcovitiNote(value.mildew.notes.grapes)
                            ? `${value.mildew.notes.grapes}`
                            : undefined,
                        oidiumLeaves: isEcovitiNote(value.oidium.notes.leaves)
                            ? `${value.oidium.notes.leaves}`
                            : undefined,
                        oidiumGrapes: isEcovitiNote(value.oidium.notes.grapes)
                            ? `${value.oidium.notes.grapes}`
                            : undefined
                    }
                };
                // Adding to the list if acc is empty or if the notes are different
                if (isEmpty(acc) ||
                    (!isNil(previousNote) && !isEqualWith(newNote, previousNote, isSameNote))) {
                    acc.push(newNote);
                }
            }
            return acc;
        }, []);
    }, [decitraitSummary]);
    const { isLoading, isFetching } = mergeQueryState(summaryRequest, plotRequest);
    const refetch = summaryRequest.refetch;
    return { isLoading, isFetching, plot, refetch, sanitaryStateNotes };
};
